'use client';

import { useTranslations } from 'next-intl';
import React, { useEffect } from 'react';

import CalendarBar, { TrailingItemVisibility } from '@/app/[locale]/(default-layout)/program/_components/CalendarBar';
import ProgramListCalendarBarItem from '@/app/[locale]/(default-layout)/program/_components/ProgramListCalendarBarItem';

import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';
import { formatDate_yyyyMMdd } from '@/utils/date-helper';

type ProgramListCalendarProps = {
    visibleDaysInFuture: number;
    totalShowingsDaily: number[];
    showingsAllCount?: number;
    showTrailingItem?: TrailingItemVisibility;
    showLeadingItem?: TrailingItemVisibility;
    selectedDate: string | 'all';
    trailingItem?: React.JSX.Element;
    /**
     * A date selected outside the visible `visibleDaysInFuture` range (e.g. picked via the date
     * picker). When set, an extra — always-selected — date box is appended to the slider for it,
     * mirroring cineamo.com. Cleared automatically once a date within the range (or "all") is selected.
     */
    outOfRangeDate?: Date;
    outOfRangeShowingsCount?: number;
    onSelectedDateChange: (selectedDate: string | 'all') => void;
    viewBelowCalendarItem?: (date: Date | 'all' | 'soon', showingsCount: number) => React.JSX.Element;
    isCalendarBarItemAlwaysHighlighted?: boolean;
    widerItemSizeOnMobile?: boolean;
    widthMinSoonItem?: boolean;
    isLoadingShowingsNumber?: boolean;
};

function ProgramListCalendar(props: ProgramListCalendarProps): React.JSX.Element {
    const {
        visibleDaysInFuture,
        totalShowingsDaily,
        showingsAllCount,
        showLeadingItem,
        showTrailingItem,
        selectedDate,
        trailingItem,
        outOfRangeDate,
        outOfRangeShowingsCount,
        onSelectedDateChange,
        viewBelowCalendarItem,
        isCalendarBarItemAlwaysHighlighted,
        widerItemSizeOnMobile,
        isLoadingShowingsNumber,
        widthMinSoonItem
    } = props;
    const domainConfiguration = useDomainConfiguration();

    const t = useTranslations();

    // Shared item colours (from the cinema's domain theme) — used by every calendar box.
    const calendarItemStyle = domainConfiguration
        ? {
              backgroundColor: domainConfiguration.style.surfaceColor,
              backgroundColorItemActive: domainConfiguration.style.primaryColor,
              textColor: domainConfiguration.style.onSurfaceColor,
              textColorItemActive: domainConfiguration.style.onPrimaryColor
          }
        : undefined;

    const firstColumnWithShowings = totalShowingsDaily.findIndex((it) => it > 0);

    useEffect(() => {
        if (isCalendarBarItemAlwaysHighlighted) {
            return;
        }
        if (window && typeof window !== 'undefined') {
            const scrollTarget =
                // go to selected day if it has showings
                window.document.querySelector(
                    '.__selected__calendar__bar__date.__scroll__target__calendar__bar__date'
                ) ??
                // otherwise go to nearest day with showings
                window.document.getElementsByClassName('__scroll__target__calendar__bar__date')?.[0] ??
                // or to the selected day if there are no showings for any day
                window.document.getElementsByClassName('__selected__calendar__bar__date')?.[0];
            scrollTarget?.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'center'
            });
        }
    }, [totalShowingsDaily, isCalendarBarItemAlwaysHighlighted, firstColumnWithShowings]);

    // Scroll the *selected* box into view whenever the selection changes (like cineamo.com). Unlike
    // the effect above — which targets the nearest day with showings on load — this always centers
    // the selected box, which is essential for an out-of-range date whose box is appended at the far
    // end of the slider (otherwise it lands off-screen and looks like nothing happened).
    useEffect(() => {
        if (isCalendarBarItemAlwaysHighlighted || typeof window === 'undefined') {
            return;
        }
        const selectedItem = window.document.getElementsByClassName('__selected__calendar__bar__date')?.[0];
        selectedItem?.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'center'
        });
    }, [selectedDate, isCalendarBarItemAlwaysHighlighted]);

    const disableScrollIntoView = isCalendarBarItemAlwaysHighlighted
        ? typeof showTrailingItem === 'object' && showTrailingItem.mobile !== 'scroll'
        : true;

    return (
        <div
            role="group"
            aria-label={t('program.date-filter')}>
            <CalendarBar
                visibleDaysInFuture={visibleDaysInFuture}
                selectedDate={selectedDate === 'all' ? 'all' : formatDate_yyyyMMdd(selectedDate)}
                setSelectedDate={(date) => onSelectedDateChange(date)}
                disableScrollIntoView={disableScrollIntoView}
                customCalendarBarItem={(index, props) => (
                    <ProgramListCalendarBarItem
                        {...props}
                        id={index.toString()}
                        key={`ProgramListCalendarBarItem#${index}`}
                        isAlwaysHighlighted={isCalendarBarItemAlwaysHighlighted}
                        isDisabled={isCalendarBarItemAlwaysHighlighted}
                        widerItemSizeOnMobile={widerItemSizeOnMobile}
                        showingsNumber={{
                            value: totalShowingsDaily.at(index) ?? 0,
                            isLoading: isLoadingShowingsNumber
                        }}
                        viewBelow={
                            viewBelowCalendarItem
                                ? (date) => viewBelowCalendarItem(date, totalShowingsDaily.at(index) ?? 0)
                                : undefined
                        }
                    />
                )}
                calendarItemStyle={calendarItemStyle}
                extraDateItem={
                    outOfRangeDate ? (
                        <ProgramListCalendarBarItem
                            id="out-of-range"
                            key="ProgramListCalendarBarItem#out-of-range"
                            date={outOfRangeDate}
                            selectedDate={formatDate_yyyyMMdd(outOfRangeDate)}
                            setSelectedDate={(date) => onSelectedDateChange(date)}
                            widerItemSizeOnMobile={widerItemSizeOnMobile}
                            showingsNumber={{
                                value: outOfRangeShowingsCount ?? 0,
                                isLoading: isLoadingShowingsNumber
                            }}
                            calendarItemStyle={calendarItemStyle}
                            viewBelow={
                                viewBelowCalendarItem
                                    ? (date) => viewBelowCalendarItem(date, outOfRangeShowingsCount ?? 0)
                                    : undefined
                            }
                        />
                    ) : undefined
                }
                showLeadingItem={showLeadingItem}
                leadingItem={
                    showLeadingItem && (
                        <ProgramListCalendarBarItem
                            classNameProgramListCalendarBarItem="box-border relative px-12 uppercase"
                            title={t('common.all')}
                            isItemActive={selectedDate === 'all'}
                            isAlwaysHighlighted={isCalendarBarItemAlwaysHighlighted}
                            isDisabled={isCalendarBarItemAlwaysHighlighted}
                            widerItemSizeOnMobile={widerItemSizeOnMobile}
                            onItemClick={() => onSelectedDateChange('all')}
                            widthMin={widthMinSoonItem !== false}
                            showingsNumber={{
                                value: showingsAllCount,
                                isLoading: isLoadingShowingsNumber
                            }}
                            calendarItemStyle={calendarItemStyle}
                            viewBelow={
                                viewBelowCalendarItem ? () => viewBelowCalendarItem('all', showingsAllCount) : undefined
                            }
                        />
                    )
                }
                showTrailingItem={showTrailingItem}
                trailingItem={showTrailingItem && trailingItem}
            />
        </div>
    );
}

export default ProgramListCalendar;
